import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #priceUpdateTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    clearTimeout(this.#priceUpdateTimer);
  }

  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const resource = event.detail.resource;
    if (!resource) return;

    // Cancel any pending price update before scheduling a new one
    clearTimeout(this.#priceUpdateTimer);

    this.#priceUpdateTimer = setTimeout(() => {
      const price = resource.price;
      const compareAtPrice = resource.compare_at_price;
      const hasCompare = compareAtPrice && compareAtPrice > price;

      const fmt = (cents) => '$' + (cents / 100).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' AUD';

      const salePriceEl = this.querySelector('.price-item--sale.price');
      const compareEl = this.querySelector('.price-item--regular.compare-at-price');
      const regularPriceEl = this.querySelector('.price__regular .price');
      const priceRegular = this.querySelector('.price__regular');
      const priceSale = this.querySelector('.price__sale');

      if (salePriceEl) salePriceEl.textContent = fmt(price);
      if (compareEl) compareEl.textContent = fmt(compareAtPrice);
      if (regularPriceEl) regularPriceEl.textContent = fmt(price);

      if (priceRegular && priceSale) {
        priceRegular.classList.toggle('price__hidden', !!hasCompare);
        priceSale.classList.toggle('price__hidden', !hasCompare);
      }

      const input_selector = '#product-form-installment-' + this.dataset.blockId + ' input[name="id"]';
      const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
      if (installmentsInput) {
        installmentsInput.value = resource.id ?? '';
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 750);
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}